import { NotificationsProvider } from '@ijoin/components';
import { CssBaseline, LinearProgress, ThemeProvider } from '@mui/material';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';

import { DefaultErrorComponent, DefaultNotFoundComponent } from './components';
import { PUBLIC_PATHS } from './consts';
import { routeTree } from './routeTree.gen';
import { theme } from './theme';

let isLoggingOut = false;

/* a dead session fails every mounted query at once */
const handleUnauthorized = (error: unknown) => {
  if (
    (error as { status?: number })?.status !== 401 ||
    isLoggingOut ||
    PUBLIC_PATHS.includes(window.location.pathname)
  ) {
    return;
  }

  isLoggingOut = true;

  window.fetch('/api/v2/authentication/admins', {
    credentials: 'include',
    keepalive: true,
    method: 'DELETE'
  });
  localStorage.removeItem('isAdmin');
  window.location.href = '/login';
};

const queryClient = new QueryClient({
  defaultOptions: {
    mutations: {
      retry: false
    },
    queries: {
      refetchOnWindowFocus: false,
      retry: false
    }
  },
  mutationCache: new MutationCache({
    onError: handleUnauthorized
  }),
  queryCache: new QueryCache({
    onError: handleUnauthorized
  })
});

const router = createRouter({
  defaultErrorComponent: props => <DefaultErrorComponent {...(props as any)} />,
  defaultNotFoundComponent: () => <DefaultNotFoundComponent />,
  defaultPendingComponent: () => <LinearProgress />,
  routeTree
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root')!);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <NotificationsProvider
          slotProps={{
            snackbar: {
              anchorOrigin: {
                horizontal: 'center',
                vertical: 'top'
              },
              autoHideDuration: 3000
            }
          }}>
          <CssBaseline />
          <RouterProvider router={router} />
        </NotificationsProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>
);
